// Docked video player (5 Oct 2026; phone card + swipes 17 Oct). Ticker videos open here, bottom right, instead of fullscreen.
// The page stays usable underneath, the video keeps playing while she scrolls, and it picks up where it left off
// on the next store page (sessionStorage, same tab) until she closes it.
(() => {
  if (window.nvDock) return;
  const KEY = 'nvDock';
  const TTL = 30 * 60 * 1000; // a tab left alone for half an hour starts clean
  const store = {
    get() { try { const s = JSON.parse(sessionStorage.getItem(KEY)); return s && Date.now() - s.at < TTL ? s : null; } catch (e) { return null; } },
    set(s) { try { sessionStorage.setItem(KEY, JSON.stringify({ ...s, at: Date.now() })); } catch (e) {} },
    clear() { try { sessionStorage.removeItem(KEY); } catch (e) {} },
  };
  const svg = (d, w = 14) => `<svg viewBox="0 0 16 16" width="${w}" height="${w}" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
  const icon = {
    close: svg('<path d="M4 4l8 8M12 4l-8 8"/>'),
    shrink: svg('<path d="M3.5 6l4.5 4.5L12.5 6"/>', 18),
    up: svg('<path d="M4 10l4-4 4 4"/>'),
    down: svg('<path d="M4 6l4 4 4-4"/>'),
    on: svg('<path d="M2.5 6h2.2L8 3.2v9.6L4.7 10H2.5z"/><path d="M10.6 5.6a3.4 3.4 0 0 1 0 4.8M12.4 3.8a6 6 0 0 1 0 8.4"/>', 18),
    off: svg('<path d="M2.5 6h2.2L8 3.2v9.6L4.7 10H2.5z"/><path d="M11 6.2l3.4 3.6M14.4 6.2L11 9.8"/>', 18),
    play: '<svg viewBox="0 0 16 16" width="20" height="20" aria-hidden="true"><path d="M5.5 3.6v8.8L12.5 8z" fill="currentColor"/></svg>',
  };

  // Two <video> elements, swapped (17 Oct): the next video loads underneath while the current one stays on screen, so
  // the up/down arrows never flash black; the spare then preloads the following video so the next press is instant.
  // Phones (17 Oct, Nate): a tap opens the player as an enlarged centred card over a dimmed page (not edge to edge);
  // swipe up/down = next/previous, pull down from the top of the card, tap outside or the chevron = shrink to the little
  // player bottom right. Desktop keeps the little player only.
  // Focus mode (18 Oct, Nate, desktop product page): the ticker hands one video over to a centred, slightly enlarged,
  // locked card that loops with sound. X, Escape or a click anywhere off it closes the card and hands the video back to
  // the ticker (which then carries on playing it silently); it never drops to the little player above the sticky cart.
  const phone = matchMedia('(max-width: 749px)');
  let el, video, spare, media, bar, shop, focus = null, upgrading = false, savedOverflow = null, items = [], index = 0, wantSound = true, raf = 0, lastSave = 0, liftObs, seq = 0, unmuteArmed = false;

  const save = () => {
    if (!el?.classList.contains('is-open') || !items.length || focus) return; // a focused video isn't carried to the next page
    store.set({ items, i: index, t: video.currentTime || 0, sound: wantSound });
  };

  // Only one thing on the page talks at a time (17 Oct): when this player has sound it tells the others, and it pauses
  // itself when another player (e.g. Eleni's timeline video) turns its sound on.
  const announce = () => document.dispatchEvent(new CustomEvent('nv:sound', { detail: { from: 'dock' } }));
  document.addEventListener('nv:sound', (e) => {
    if (e.detail?.from === 'dock' || !el?.classList.contains('is-open') || video.muted || video.paused) return;
    video.pause(); el.classList.add('is-paused'); save();
  });

  const setSound = (on) => {
    wantSound = on;
    video.muted = !on;
    if (on) announce();
    const s = el.querySelector('.nv-dock__sound');
    s.innerHTML = on ? icon.on : icon.off;
    s.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
    s.setAttribute('aria-pressed', String(on));
    save();
  };

  // 20 Oct (Nate: bar choppy on iPhone): Safari moves currentTime in steps and throttles rAF while video plays, so a per-frame
  // update stuttered. Now one linear animation runs from here to the end on the compositor, re-synced on play/pause/seek/stall/swap.
  let barAnim = null, barFrom = null;
  const stopBar = (p) => { barAnim?.cancel(); barAnim = null; barFrom = null; if (p != null) bar.style.transform = `scaleX(${p})`; };
  const tick = () => {
    cancelAnimationFrame(raf);
    const d = video.duration, t = video.currentTime || 0, rate = video.playbackRate || 1;
    const p = d && isFinite(d) ? Math.min(1, t / d) : 0;
    stopBar(p);
    if (video.paused || !d || !isFinite(d) || video.readyState < 3) return;
    if (!bar.animate) { raf = requestAnimationFrame(tick); return; }
    barAnim = bar.animate([{ transform: `scaleX(${p})` }, { transform: 'scaleX(1)' }], { duration: Math.max(0, (d - t) * 1000 / rate), easing: 'linear', fill: 'forwards' });
    barFrom = { t, at: performance.now(), rate };
  };
  // Light drift check on timeupdate (~4x a second): only re-sync when the bar and the video are 0.3 s apart
  const checkBar = () => {
    if (video.paused) return;
    if (!barFrom) return tick();
    const want = barFrom.t + (performance.now() - barFrom.at) / 1000 * barFrom.rate;
    if (Math.abs(want - (video.currentTime || 0)) > 0.3) tick();
  };

  const play = () => { const v = video; return v.play().then(() => { if (v !== video) return; el.classList.remove('is-paused'); tick(); if (!v.muted) announce(); }).catch((err) => {
    // A newer swipe/press replaced this video before it started (AbortError): not a real failure, leave it alone
    // (17 Oct audit: the old fallback muted the NEW video and showed the muted icon after fast swipes)
    if (v !== video || err?.name === 'AbortError') return;
    // Browsers block sound on a video nobody tapped (e.g. after moving to the next page): carry on muted
    // and turn the sound back on at her next touch.
    if (!video.muted) {
      // No sound prompt button (Nate, 17 Oct): carry on muted and switch the sound on by itself at her very next
      // touch, click or key anywhere on the page (browsers allow sound inside that gesture). Armed once, never stacked.
      video.muted = true;
      const s = el.querySelector('.nv-dock__sound');
      s.innerHTML = icon.off; s.setAttribute('aria-label', 'Turn sound on'); s.setAttribute('aria-pressed', 'false');
      if (!unmuteArmed) {
        unmuteArmed = true;
        // pointerup/touchend/keydown are real "user gestures" on phones too (a touch pointerdown isn't). Taps on the player
        // itself are left to the player (its video/speaker handlers unmute), so the same tap can't unmute then re-mute/pause.
        const evs = ['pointerup', 'touchend', 'keydown'];
        const unmute = (e) => { if (e && el.contains(e.target)) return; unmuteArmed = false; evs.forEach((t) => removeEventListener(t, unmute, true)); if (el.classList.contains('is-open') && wantSound) { setSound(true); video.play().catch(() => {}); } };
        evs.forEach((t) => addEventListener(t, unmute, { capture: true, passive: true }));
      }
      return video.play().then(() => { el.classList.remove('is-paused'); tick(); }).catch(() => el.classList.add('is-paused'));
    }
    el.classList.add('is-paused');
  }); };

  const here = (url) => { try { return new URL(url, location.href).pathname === location.pathname; } catch (e) { return false; } };

  const same = (v, src) => !!src && v.dataset.src === src;
  const setSrc = (v, src) => { v.dataset.src = src || ''; if (src) { v.preload = 'auto'; v.src = src; } else { v.removeAttribute('src'); v.load(); } };

  // Quietly load the following video into the spare element (muted, paused) so the next arrow press is instant
  const preloadNext = () => {
    if (items.length < 2 || upgrading) return;
    const nx = items[(index + 1) % items.length];
    if (same(spare, nx.src)) return;
    spare.muted = true;
    setSrc(spare, nx.src);
  };

  // Warm start (18 Oct, Nate: "jittery, then it loads"): the ticker card is already playing a small copy of this video.
  // The player starts on that copy at the exact same frame (it's cached, so no wait and no jump from the poster), then
  // quietly loads the HD file into the spare element, lines it up a moment ahead and crossfades to it.
  const upgrade = (my, src) => {
    const v2 = spare;
    upgrading = true;
    v2.muted = true;
    setSrc(v2, src);
    const fail = () => { if (my === seq) { upgrading = false; preloadNext(); } };
    v2.addEventListener('error', fail, { once: true });
    v2.addEventListener('canplay', () => {
      if (my !== seq) return fail();
      let tries = 0;
      const onSeeked = () => {
        if (my !== seq) return fail();
        // Seeking takes a moment, so the HD copy can land a little behind: line it up again (twice at most)
        const lag = (video.currentTime || 0) - v2.currentTime;
        if (lag > 0.06 && tries++ < 2) { v2.addEventListener('seeked', onSeeked, { once: true }); v2.currentTime = Math.min(video.currentTime + lag + 0.15, Math.max(0, (v2.duration || 1) - 0.2)); return; }
        v2.play().then(() => {
          if (my !== seq || v2 === video) return fail();
          const old = video;
          v2.muted = old.muted;
          old.muted = true;               // never two soundtracks, even for a frame
          video = v2; spare = old;
          video.classList.add('is-on');
          old.classList.remove('is-on');
          setTimeout(() => { old.pause(); upgrading = false; if (my === seq) preloadNext(); }, 320);
          tick();
        }).catch(fail);
      };
      v2.addEventListener('seeked', onSeeked, { once: true });
      v2.currentTime = Math.min((video.currentTime || 0) + 0.3, Math.max(0, (v2.duration || 1) - 0.2));
    }, { once: true });
  };

  const load = (i, t = 0) => {
    const my = ++seq;
    upgrading = false;
    index = (i + items.length) % items.length;
    const it = items[index];
    // One-time warm start from the ticker card's own (already loaded) preview
    // Phones open full screen, where the 480p→HD swap a moment later showed as a jump: phones go straight to HD from the start
    const warm = it.warm && (focus || !phone.matches) && !same(video, it.src) && !same(spare, it.src) ? it.warm : null;
    if (it.warm && !warm) { it.warm = null; t = 0; }
    if (warm) { t = it.wt || t; it.warm = null; }
    const srcNow = warm || it.src;
    // The poster sits behind both videos, so the very first open shows her photo, never a black box
    media.style.backgroundImage = it.poster ? `url("${it.poster}")` : '';
    const p = it.p;
    shop.hidden = !p;
    if (p) {
      shop.querySelectorAll('a').forEach((a) => { a.href = p.url; });
      const img = shop.querySelector('img');
      if (p.img) img.src = p.img; else img.removeAttribute('src');
      shop.querySelector('b').textContent = p.title || '';
      shop.querySelector('small').textContent = p.price || '';
    }
    el.setAttribute('aria-label', `Video: ${it.title || 'Nuvana'}`);

    // Same video already on screen (e.g. reopened): just seek and play
    let inc = same(video, srcNow) ? video : spare;
    if (inc === spare && !same(spare, srcNow)) setSrc(spare, srcNow);
    inc.muted = !wantSound;
    if (inc !== video) inc.pause();

    const show = () => {
      if (my !== seq) return; // she pressed again before this one was ready; the newer press wins
      stopBar(0);
      if (inc !== video) {
        const old = video;
        video = inc; spare = old;
        video.classList.add('is-on');
        old.classList.remove('is-on');
        old.pause();
        // Let the fade finish before the old one is reused for preloading
        setTimeout(() => { if (my === seq) (warm ? upgrade(my, it.src) : preloadNext()); }, 260);
      } else if (warm) upgrade(my, it.src); else preloadNext();
      play();
      save();
    };
    const seekThen = () => {
      const target = t > 0 ? Math.min(t, Math.max(0, (inc.duration || t) - 0.5)) : 0;
      if (Math.abs((inc.currentTime || 0) - target) < 0.05) return show();
      inc.addEventListener('seeked', show, { once: true });
      inc.currentTime = target;
    };
    if (inc.readyState >= 2) seekThen();
    else inc.addEventListener('loadeddata', () => { if (my === seq) seekThen(); }, { once: true });
    // A video that won't load (bad link, network): skip to the next one; if every one fails, show the play icon
    inc.addEventListener('error', () => {
      if (my !== seq) return;
      it.failed = true;
      if (items.some((x) => !x.failed)) load(index + 1); else el.classList.add('is-paused');
    }, { once: true });
  };

  // Sit above the floating add-to-cart bar on the product page whenever it's showing
  const lift = () => {
    const wrap = document.querySelector('.nv-sticky-purchase');
    const inner = wrap?.querySelector('.nv-sticky-inner');
    let px = 0;
    if (inner && !wrap.hidden && !wrap.classList.contains('is-off')) {
      const r = inner.getBoundingClientRect();
      if (r.height) px = Math.max(0, innerHeight - r.top);
    }
    el.style.setProperty('--lift', `${Math.round(px)}px`);
  };
  const watchLift = () => {
    const wrap = document.querySelector('.nv-sticky-purchase');
    liftObs?.disconnect();
    if (wrap) { liftObs = new MutationObserver(() => { lift(); setTimeout(lift, 520); }); liftObs.observe(wrap, { attributes: true, subtree: true, attributeFilter: ['class', 'hidden', 'style'] }); }
    lift();
  };

  const closeBtn = () => el.querySelector('.nv-dock__close');
  // Leaving the focused card: close the player and tell the ticker where the video had got to
  // It fades out where it is (never jumps to the corner first), and the ticker gets the video back straight away.
  const exitFocus = () => {
    const done = focus, t = video?.currentTime || 0, i = index;
    focus = null;
    video.muted = true;
    el.classList.add('is-leaving');
    try { done({ i, t }); } catch (e) {}
    setTimeout(() => { if (focus) return; close(); el.classList.remove('is-focus', 'is-leaving'); }, 260);
  };
  const setFull = (on) => {
    if (!on && focus) { exitFocus(); return; }
    if (on === el.classList.contains('is-full')) return;
    el.classList.toggle('is-full', on);
    // Full screen = the only video playing: the rail previews behind it pause (nuvana-videos checks this class) and resume after
    document.documentElement.classList.toggle('nv-dock-full', on);
    if (on) document.querySelectorAll('.nv-video-card__preview').forEach((v) => { if (!v.paused) v.pause(); });
    const b = closeBtn();
    b.innerHTML = on && !focus ? icon.shrink : icon.close;
    b.setAttribute('aria-label', on && !focus ? 'Shrink video' : 'Close video');
    el.setAttribute('role', on ? 'dialog' : 'region');
    if (on) el.setAttribute('aria-modal', 'true'); else el.removeAttribute('aria-modal');
    const root = document.documentElement;
    if (on) { if (savedOverflow === null) savedOverflow = root.style.overflow; root.style.overflow = 'hidden'; }
    else if (savedOverflow !== null) { root.style.overflow = savedOverflow; savedOverflow = null; }
    if (!on && !el.classList.contains('is-focus')) { el.classList.add('is-shrunk'); setTimeout(() => el.classList.remove('is-shrunk'), 400); lift(); }
  };

  // Swipes on the full-screen player. A short tap still pauses/plays (handled by the video's click).
  let touch = null, swiped = false;
  const onTouchStart = (e) => {
    if (!el.classList.contains('is-full') || e.touches.length !== 1 || e.target.closest('button, a')) { touch = null; return; }
    const p = e.touches[0];
    const r = media.getBoundingClientRect();
    touch = { x: p.clientX, y: p.clientY, t: Date.now(), top: p.clientY < r.top + r.height * 0.22 };
  };
  const onTouchMove = (e) => {
    if (!touch) return;
    const dy = e.touches[0].clientY - touch.y;
    media.style.transform = `translateY(${Math.round(dy * 0.25)}px)`;
    e.preventDefault(); // keep the page (and pull-to-refresh) still underneath
  };
  const onTouchEnd = (e) => {
    if (!touch) return;
    const p = e.changedTouches[0], dx = p.clientX - touch.x, dy = p.clientY - touch.y;
    const fast = Date.now() - touch.t < 600;
    media.style.transform = '';
    const vertical = Math.abs(dy) > Math.abs(dx) && (Math.abs(dy) > 70 || (fast && Math.abs(dy) > 40));
    if (vertical) {
      swiped = true; setTimeout(() => { swiped = false; }, 350);
      if (dy > 0 && touch.top) setFull(false);          // pulled down from the top: shrink to the little player
      else load(index + (dy < 0 ? 1 : -1));              // swipe up = next, swipe down = previous
    }
    touch = null;
  };

  const build = () => {
    el = document.createElement('div');
    el.className = 'nv-dock';
    el.setAttribute('role', 'region');
    el.tabIndex = -1;
    el.innerHTML = `
      <div class="nv-dock__media">
        <video class="is-on" playsinline preload="auto"></video>
        <video playsinline preload="auto" muted aria-hidden="true"></video>
        <span class="nv-dock__bar" aria-hidden="true"><i></i></span>
        <span class="nv-dock__paused" aria-hidden="true">${icon.play}</span>
        <button type="button" class="nv-dock__btn nv-dock__close" aria-label="Close video">${icon.close}</button>
        <span class="nv-dock__nav">
          <button type="button" class="nv-dock__btn" data-step="-1" aria-label="Previous video">${icon.up}</button>
          <button type="button" class="nv-dock__btn" data-step="1" aria-label="Next video">${icon.down}</button>
        </span>
        <button type="button" class="nv-dock__btn nv-dock__sound"></button>
      </div>
      <div class="nv-dock__shop" hidden>
        <a class="nv-dock__product" tabindex="-1"><img alt="" width="44" height="44"><span><b></b><small></small></span></a>
        <a class="nv-dock__cta">Shop now</a>
      </div>`;
    document.body.append(el);
    media = el.querySelector('.nv-dock__media');
    [video, spare] = el.querySelectorAll('video');
    bar = el.querySelector('.nv-dock__bar i');
    shop = el.querySelector('.nv-dock__shop');

    el.querySelector('.nv-dock__close').addEventListener('click', () => (el.classList.contains('is-full') ? setFull(false) : close()));
    media.addEventListener('touchstart', onTouchStart, { passive: true });
    media.addEventListener('touchmove', onTouchMove, { passive: false });
    media.addEventListener('touchend', onTouchEnd, { passive: true });
    media.addEventListener('touchcancel', () => { touch = null; media.style.transform = ''; }, { passive: true });
    // On the little player, a tap on the picture opens it full screen again (phones only)
    media.addEventListener('click', (e) => {
      if (phone.matches && !el.classList.contains('is-full') && e.target.tagName === 'VIDEO') { e.stopImmediatePropagation(); setFull(true); if (video.paused) play(); }
    }, true);
    phone.addEventListener?.('change', () => { if (!phone.matches) setFull(false); });
    // Tap on the dimmed page around the card = shrink
    document.addEventListener('pointerdown', (e) => {
      if (!el.classList.contains('is-full') || el.contains(e.target)) return;
      e.preventDefault();
      setFull(false);
      // ...and swallow the click that follows, so the tap doesn't also press a link or button under the dimmed page
      const block = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
      document.addEventListener('click', block, { capture: true, once: true });
      setTimeout(() => document.removeEventListener('click', block, true), 500);
    }, true);
    el.querySelectorAll('[data-step]').forEach((b) => b.addEventListener('click', () => load(index + Number(b.dataset.step))));
    el.querySelector('.nv-dock__sound').addEventListener('click', () => setSound(!wantSound || video.muted));
    // Both elements get the same listeners; only the one on screen ever acts
    [video, spare].forEach((v) => {
      const on = (type, fn) => v.addEventListener(type, (e) => { if (e.target === video) fn(e); });
      on('click', () => {
        if (swiped) return;
        // Sound was blocked by the browser and she taps the video: that tap means "sound on", not "pause"
        if (wantSound && video.muted && !video.paused) { setSound(true); video.play().catch(() => {}); return; }
        if (video.paused) play(); else { video.pause(); el.classList.add('is-paused'); save(); }
      });
      on('ended', () => { if (items.length > 1 && !focus) load(index + 1); else { video.currentTime = 0; play(); } }); // focused = loop the same one
      on('play', tick);
      ['playing', 'seeked', 'ratechange', 'durationchange', 'waiting', 'stalled'].forEach((t) => on(t, tick));
      // If the browser pauses her video by itself (another tab's audio, a phone call, low-power mode), show the play icon
      on('pause', () => { tick(); if (el.classList.contains('is-open')) { el.classList.add('is-paused'); save(); } });
      on('playing', () => el.classList.remove('is-paused'));
      on('timeupdate', () => { checkBar(); if (Date.now() - lastSave > 2000) { lastSave = Date.now(); save(); } });
    });
    shop.addEventListener('click', (e) => {
      const a = e.target.closest('a');
      if (!a || !here(a.href)) return;
      // Already on that product: glide up to the buy box and keep the video going
      e.preventDefault();
      setFull(false); // 17 Oct audit: from the enlarged phone card the page scrolled behind the dimmed overlay
      (document.querySelector('.product-information') || document.querySelector('main'))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); if (el.classList.contains('is-full')) setFull(false); else close(); }
      else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); load(index + (e.key === 'ArrowDown' ? 1 : -1)); }
    });
    addEventListener('resize', lift, { passive: true });
    setSound(true);
  };

  function open(list, i = 0, opts = {}) {
    if (!list?.length) return;
    if (!el) build();
    if (focus && !opts.focus) { focus = null; el.classList.remove('is-focus'); }
    if (opts.focus) { focus = typeof opts.onExit === 'function' ? opts.onExit : () => {}; store.clear(); }
    el.classList.remove('is-leaving');
    el.classList.toggle('is-focus', !!opts.focus);
    items = list;
    el.querySelector('.nv-dock__nav').hidden = items.length < 2;
    el.classList.toggle('no-anim', !!opts.restored);
    el.classList.add('is-open');
    watchLift();
    if (opts.restored) { wantSound = opts.sound !== false; setSound(wantSound); }
    else { setSound(true); el.focus({ preventScroll: true }); }
    // Phones open straight to full screen from a tap; a restored player on the next page stays little; focus = centred card
    setFull(!!opts.focus || (phone.matches && !opts.restored));
    load(i, opts.t || 0);
  }

  function close() {
    if (!el) return;
    if (focus) { exitFocus(); return; }
    cancelAnimationFrame(raf);
    stopBar(0);
    seq++;
    [video, spare].forEach((v) => { v.pause(); setSrc(v, ''); });
    media.style.backgroundImage = '';
    setFull(false);
    el.classList.remove('is-open', 'no-anim', 'is-paused', 'is-shrunk');
    liftObs?.disconnect();
    store.clear();
  }

  window.nvDock = { open, close };
  addEventListener('pagehide', save);
  document.addEventListener('visibilitychange', () => { if (document.hidden) save(); });

  // Carry on from the last page
  const start = () => { const s = store.get(); if (s?.items?.length) open(s.items, s.i || 0, { t: s.t, sound: s.sound, restored: true }); };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
